import { Injectable, InjectionToken, computed, inject, signal } from '@angular/core';
import {
  AccountInfo,
  AuthError,
  Configuration,
  IPublicClientApplication,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';

// Registered as a redirect URI of the single-page application in Entra ID, per host.
export const SIGN_IN_PATH = '/signin-oidc';

const RETURN_TO_KEY = 'solvind.returnTo';

// MSAL errors that mean "the user has to sign in again" rather than "something is broken".
const INTERACTION_CODES = new Set([
  'no_tokens_found',
  'no_account_error',
  'user_login_error',
  'monitor_window_timeout',
  'timed_out',
]);

export function buildMsalConfiguration(): Configuration {
  return {
    auth: {
      clientId: environment.auth.clientId,
      authority: `https://login.microsoftonline.com/${environment.auth.tenantId}`,
      // The page's own origin, so the same build works on localhost and on every deployed host.
      redirectUri: `${window.location.origin}${SIGN_IN_PATH}`,
    },
    // Not localStorage: the tokens die with the tab.
    cache: { cacheLocation: 'sessionStorage' },
  };
}

// A seam for the tests: they hand in a fake instead of the real library.
export const MSAL_FACTORY = new InjectionToken<(config: Configuration) => IPublicClientApplication>(
  'MSAL_FACTORY',
  { providedIn: 'root', factory: () => (config) => new PublicClientApplication(config) },
);

// Only a path inside this app is ever returned to.
function isLocalPath(path: string | null): path is string {
  return (
    path !== null &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.startsWith(SIGN_IN_PATH)
  );
}

// The pending promise stands for a page that is about to be replaced by the sign-in page.
function leavingThePage(): Promise<never> {
  return new Promise<never>(() => undefined);
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly createClient = inject(MSAL_FACTORY);
  private msal: IPublicClientApplication | null = null;

  readonly account = signal<AccountInfo | null>(null);
  // Set when signing in failed; the page then offers to try again instead of loading data.
  readonly signInError = signal<string | null>(null);
  readonly userName = computed(() => {
    const account = this.account();
    return account === null ? '' : account.name || account.username;
  });

  // Runs before the app starts: completes only when there is a signed-in user, or sign-in failed.
  async initialize(): Promise<void> {
    try {
      const msal = this.createClient(buildMsalConfiguration());
      await msal.initialize();
      this.msal = msal;

      // Navigation is done here (not by MSAL) so that the address of the page the user came from is restored.
      const result = await msal.handleRedirectPromise({ navigateToLoginRequestUrl: false });
      const account = result?.account ?? msal.getAllAccounts()[0] ?? null;
      if (account !== null) {
        msal.setActiveAccount(account);
        this.account.set(account);
        this.restoreStartPage();
        return;
      }

      await this.signIn();
      await leavingThePage();
    } catch (error) {
      this.signInError.set(describe(error));
    }
  }

  async signIn(): Promise<void> {
    const msal = this.requireClient();
    this.rememberStartPage();
    await msal.loginRedirect({ scopes: environment.auth.scopes });
  }

  async signOut(): Promise<void> {
    const msal = this.requireClient();
    await msal.logoutRedirect({ account: this.account() ?? undefined });
  }

  // A token for the API; renewed quietly, and when that is not possible the page goes to the sign-in page.
  async getAccessToken(): Promise<string> {
    const msal = this.requireClient();
    const account = this.account();
    if (account === null) {
      throw new Error('Not signed in.');
    }

    try {
      const result = await msal.acquireTokenSilent({ scopes: environment.auth.scopes, account });
      return result.accessToken;
    } catch (error) {
      if (!needsInteraction(error)) {
        throw error;
      }
      this.rememberStartPage();
      // A second request that needs a token while the first one is redirecting is refused by MSAL; the page is leaving anyway.
      await msal
        .acquireTokenRedirect({ scopes: environment.auth.scopes, account })
        .catch(() => undefined);
      return leavingThePage();
    }
  }

  private requireClient(): IPublicClientApplication {
    if (this.msal === null) {
      throw new Error('Authentication is not initialized.');
    }
    return this.msal;
  }

  private rememberStartPage(): void {
    const current = window.location.pathname + window.location.search;
    sessionStorage.setItem(RETURN_TO_KEY, isLocalPath(current) ? current : '/');
  }

  // Back to the page the user was on before signing in, and off the sign-in address.
  private restoreStartPage(): void {
    const stored = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    if (window.location.pathname.startsWith(SIGN_IN_PATH)) {
      window.history.replaceState(null, '', isLocalPath(stored) ? stored : '/');
    }
  }
}

function needsInteraction(error: unknown): boolean {
  return (
    error instanceof InteractionRequiredAuthError ||
    (error instanceof AuthError && INTERACTION_CODES.has(error.errorCode))
  );
}

function describe(error: unknown): string {
  const code = error instanceof AuthError ? error.errorCode : null;
  return code === null ? 'Signing in failed.' : `Signing in failed (${code}).`;
}
